import React, { memo, useCallback } from 'react';
import { useAppStore } from '../store/useAppStore';
import { ChatBubbleLeftEllipsisIcon } from './Icons';

interface ChatButtonProps {
  className?: string;
}

/**
 * ChatButton Component
 * AgriConnect Notification System
 * Mobile-first, offline-ready notification indicator for PWA.
 */
export const ChatButton: React.FC<ChatButtonProps> = memo(({ className = "" }) => {
  const { totalUnreadCount, isChatOpen, setChatOpen, isOnline } = useAppStore();

  const handleToggle = useCallback(() => {
    setChatOpen(!isChatOpen);
    
    // Simulate haptic feedback if supported
    if ('vibrate' in navigator) {
      navigator.vibrate(10);
    }
  }, [isChatOpen, setChatOpen]);

  // ARIA label logic
  const ariaLabel = totalUnreadCount > 0 
    ? `Chat, ${totalUnreadCount} unread message${totalUnreadCount === 1 ? '' : 's'}` 
    : 'Chat, no new messages';

  return (
    <div className={`relative ${className}`}>
      <button
        onClick={handleToggle}
        aria-label={ariaLabel}
        aria-expanded={isChatOpen}
        className={`group relative flex items-center justify-center bg-btn-primary text-btn-primary-text p-4 rounded-full shadow-2xl hover:bg-btn-primary-hover transition-all transform hover:scale-110 active:scale-95 z-50 min-w-[56px] min-h-[56px] ${!isOnline ? 'opacity-90 grayscale-[0.2]' : ''}`}
      >
        <ChatBubbleLeftEllipsisIcon className="w-6 h-6" />
        
        {/* Unread Notification Dot */}
        {totalUnreadCount > 0 && (
          <div
            className={`absolute -top-1 -right-1 flex items-center justify-center bg-error-text text-white rounded-full border-2 border-white font-medium text-[12px] leading-none shadow-lg transition-all duration-200 transform animate-in zoom-in-50 ${
              totalUnreadCount > 9 ? 'w-6 h-6 px-1' : 'w-5 h-5'
            }`}
            aria-hidden="true"
          >
            {totalUnreadCount > 99 ? '99+' : totalUnreadCount}
          </div>
        )}

        {/* Tooltip for desktop accessibility */}
        <span className="absolute right-full mr-3 bg-brand-green text-white text-[10px] font-black uppercase px-2 py-1 rounded whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity hidden sm:block pointer-events-none">
          {isChatOpen ? 'Close Messages' : 'Messages'}
        </span>
        
        {/* Offline Status Indicator variant on the dot/button */}
        {!isOnline && (
           <div 
             className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-app-text-secondary rounded-full border-2 border-white" 
             title="Offline: Messages will sync later"
           />
        )}
      </button>

      {/* Screen reader only live region for count updates */}
      <div className="sr-only" role="status" aria-live="polite">
        {totalUnreadCount > 0 ? `${totalUnreadCount} new messages arrived.` : ''}
      </div>
    </div>
  );
});

ChatButton.displayName = 'ChatButton';
